/**
 * Product CRM Query Hooks
 *
 * Hooks for fetching product CRM analytics data including:
 * - Product statistics
 * - Partner statistics with preference filtering
 * - Rolling aggregates (time series)
 * - Product orders
 */

import api from "@/app/api";
import type {
  ProductCRMFilters,
  ProductCRMStatisticsResponse,
  ProductPartnerFilters,
  PaginatedPartnerStatisticsResponse,
  ProductRollingFilters,
  ProductRollingAggregatesResponse,
  ProductOrdersFilters,
  ProductOrdersResponse,
} from "@/app/api/types/product";
import { useQuery } from "@tanstack/react-query";
import { UUID } from "crypto";
import { productKeys } from "../keys/products";

/**
 * Fetch product CRM statistics
 *
 * Returns aggregated statistics for a product's order history.
 */
export function useProductCRMStatistics(
  productId: UUID | null,
  filters: ProductCRMFilters = {},
) {
  return useQuery({
    queryKey: productKeys.crmStatistics(productId!, filters),
    queryFn: () => api.products.getProductCRMStatistics(productId!, filters),
    enabled: !!productId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch product partner statistics with preference filtering
 *
 * Returns paginated list of partners who ordered this product,
 * with their statistics and preference data.
 * Supports filtering by preference levels.
 */
export function useProductCRMPartners(
  productId: UUID | null,
  filters: ProductPartnerFilters = {},
) {
  return useQuery({
    queryKey: productKeys.crmPartners(productId!, filters),
    queryFn: () => api.products.getProductCRMPartners(productId!, filters),
    enabled: !!productId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch product rolling aggregates (time series)
 *
 * Returns daily totals with rolling sum calculations.
 * Requires start_date and end_date.
 */
export function useProductCRMRolling(
  productId: UUID | null,
  filters: ProductRollingFilters | null,
) {
  return useQuery({
    queryKey: productKeys.crmRolling(productId!, filters!),
    queryFn: () => api.products.getProductCRMRolling(productId!, filters!),
    enabled: !!productId && !!filters?.start_date && !!filters?.end_date,
    staleTime: 5 * 60 * 1000, // 5 minutes - rolling data less volatile
  });
}

/**
 * Fetch product orders
 *
 * Returns list of all orders containing this product.
 * Supports filtering by date range and partner.
 */
export function useProductCRMOrders(
  productId: UUID | null,
  filters: ProductOrdersFilters = {},
) {
  return useQuery({
    queryKey: productKeys.crmOrders(productId!, filters),
    queryFn: () => api.products.getProductCRMOrders(productId!, filters),
    enabled: !!productId,
    staleTime: 30 * 1000, // 30 seconds - orders change frequently
  });
}
